import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Trash2, RefreshCw, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRINT_TYPE_OPTIONS = [
  { id: 1, label: 'DTG' },
  { id: 2, label: 'All Over Print' },
  { id: 5, label: 'Accessories' },
  { id: 17, label: 'DTF' },
];

const PLACEMENT_OPTIONS = [
  { key: 'fr', label: 'Front' },
  { key: 'bk', label: 'Back' },
];

export default function QikinkConfig() {
  const navigate = useNavigate();
  const [token, setToken] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [testing, setTesting] = useState(false);
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sizes, setSizes] = useState({});

  // New mapping form
  const [newMapping, setNewMapping] = useState({
    size_key: '', sku: '', print_type_id: 17, width_inches: '', height_inches: '', placement_sku: 'fr',
  });
  const [saving, setSaving] = useState(false);

  const getToken = useCallback(() => {
    const t = localStorage.getItem('admin_token');
    setToken(t);
    return t;
  }, []);

  const headers = useCallback(() => ({
    Authorization: `Bearer ${getToken()}`,
  }), [getToken]);

  useEffect(() => {
    getToken();
    fetchData();
  }, []); // eslint-disable-line

  const fetchData = async () => {
    setLoading(true);
    try {
      const [mappingsRes, pricingRes] = await Promise.all([
        axios.get(`${API}/qikink/mappings`, { headers: headers() }),
        axios.get(`${API}/print-pricing`),
      ]);
      setMappings(mappingsRes.data.mappings || []);
      setSizes(pricingRes.data.sizes || {});
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const testConnection = async () => {
    setTesting(true);
    try {
      const res = await axios.get(`${API}/qikink/test`, { headers: headers() });
      setConnectionStatus(res.data);
      if (res.data.success) toast.success('Connected to Qikink API');
      else toast.error(`Connection failed: ${res.data.error}`);
    } catch (e) {
      setConnectionStatus({ success: false, error: 'Request failed' });
      toast.error('Connection test failed');
    } finally {
      setTesting(false);
    }
  };

  const saveMapping = async () => {
    if (!newMapping.size_key || !newMapping.sku) {
      toast.error('Size and SKU are required');
      return;
    }
    setSaving(true);
    try {
      const sizeInfo = sizes[newMapping.size_key];
      await axios.post(`${API}/qikink/mappings`, {
        ...newMapping,
        label: sizeInfo ? sizeInfo.label : newMapping.size_key,
      }, { headers: headers() });
      toast.success(`Mapping saved for ${newMapping.size_key}`);
      setNewMapping({ size_key: '', sku: '', print_type_id: 17, width_inches: '', height_inches: '', placement_sku: 'fr' });
      fetchData();
    } catch (e) {
      toast.error('Failed to save mapping');
    } finally {
      setSaving(false);
    }
  };

  const deleteMapping = async (sizeKey) => {
    if (!window.confirm(`Delete mapping for ${sizeKey}?`)) return;
    try {
      await axios.delete(`${API}/qikink/mappings/${sizeKey}`, { headers: headers() });
      toast.success(`Mapping deleted for ${sizeKey}`);
      fetchData();
    } catch (e) {
      toast.error('Failed to delete mapping');
    }
  };

  const unmappedSizes = Object.keys(sizes).filter(
    (k) => !mappings.some((m) => m.size_key === k)
  );

  return (
    <div className="min-h-screen pt-24 sm:pt-32 pb-16 sm:pb-24" data-testid="qikink-config-page">
      <div className="max-w-screen-lg mx-auto px-4 sm:px-6 md:px-12">
        <button onClick={() => navigate('/admin')} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-accent mb-4 transition-colors" data-testid="back-to-admin">
          <ArrowLeft size={16} /> Back to Admin
        </button>

        <h1 className="font-serif text-2xl sm:text-3xl font-light mb-2">Qikink Configuration</h1>
        <p className="text-sm text-muted-foreground mb-8">Configure Qikink print-on-demand for automatic Indian order fulfillment</p>

        {/* Connection Test */}
        <div className="bg-card border border-border rounded-lg p-6 mb-6" data-testid="qikink-connection-section">
          <h2 className="font-sans text-sm uppercase tracking-[0.15em] font-semibold text-muted-foreground mb-4">API Connection</h2>
          <div className="flex items-center gap-4">
            <Button onClick={testConnection} disabled={testing} variant="outline" data-testid="test-connection-btn">
              {testing ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <RefreshCw size={14} className="mr-1.5" />}
              Test Connection
            </Button>
            {connectionStatus && (
              <div className={`flex items-center gap-1.5 text-sm ${connectionStatus.success ? 'text-green-600' : 'text-red-600'}`} data-testid="connection-status">
                {connectionStatus.success ? <CheckCircle size={16} /> : <XCircle size={16} />}
                {connectionStatus.success ? 'Connected' : connectionStatus.error}
              </div>
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-3">
            {connectionStatus?.success
              ? `Connected to ${connectionStatus.api_url?.includes('sandbox') ? 'Sandbox' : 'Live'} API`
              : 'Click "Test Connection" to verify. Credentials are configured in backend .env file.'}
          </p>
        </div>

        {/* SKU Mappings */}
        <div className="bg-card border border-border rounded-lg p-6 mb-6" data-testid="qikink-mappings-section">
          <h2 className="font-sans text-sm uppercase tracking-[0.15em] font-semibold text-muted-foreground mb-4">
            Print Size to Qikink SKU Mappings
          </h2>
          <p className="text-xs text-muted-foreground mb-4">
            Map each print size to a Qikink product SKU. Unmapped sizes will fall back to manual fulfillment.
            Find SKUs at <a href="https://dashboard.qikink.com" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">dashboard.qikink.com</a> &rarr; Products &rarr; SKU Descriptions.
          </p>

          {/* Existing Mappings */}
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-4"><Loader2 size={14} className="animate-spin" /> Loading...</div>
          ) : mappings.length > 0 ? (
            <div className="space-y-2 mb-6">
              {mappings.map((m) => (
                <div key={m.size_key} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg border border-border" data-testid={`mapping-${m.size_key}`}>
                  <div className="flex items-center gap-4">
                    <span className="font-serif text-base font-light min-w-[80px]">{m.label || m.size_key}</span>
                    <span className="text-xs text-muted-foreground">SKU: <code className="bg-muted px-1.5 py-0.5 rounded text-[11px]">{m.sku}</code></span>
                    <span className="text-xs text-muted-foreground">Type: {PRINT_TYPE_OPTIONS.find(p => p.id === m.print_type_id)?.label || m.print_type_id}</span>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => deleteMapping(m.size_key)} className="text-red-500 hover:text-red-700 hover:bg-red-50" data-testid={`delete-mapping-${m.size_key}`}>
                    <Trash2 size={14} />
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground py-4 mb-4">No mappings configured yet. Add your first mapping below.</p>
          )}

          {/* Add New Mapping */}
          <div className="border-t border-border pt-4">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-1.5">
              <Plus size={12} /> Add Mapping
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Print Size</Label>
                <select
                  value={newMapping.size_key}
                  onChange={(e) => setNewMapping({ ...newMapping, size_key: e.target.value })}
                  className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm"
                  data-testid="new-mapping-size"
                >
                  <option value="">Select size...</option>
                  {unmappedSizes.map((k) => (
                    <option key={k} value={k}>{sizes[k]?.label || k} ({sizes[k]?.orientation})</option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="text-xs">Qikink SKU</Label>
                <Input
                  value={newMapping.sku}
                  onChange={(e) => setNewMapping({ ...newMapping, sku: e.target.value })}
                  placeholder="e.g. MVnHs-Wh-S"
                  className="h-9"
                  data-testid="new-mapping-sku"
                />
              </div>
              <div>
                <Label className="text-xs">Print Type</Label>
                <select
                  value={newMapping.print_type_id}
                  onChange={(e) => setNewMapping({ ...newMapping, print_type_id: parseInt(e.target.value) })}
                  className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm"
                  data-testid="new-mapping-print-type"
                >
                  {PRINT_TYPE_OPTIONS.map((o) => (
                    <option key={o.id} value={o.id}>{o.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="text-xs">Design Width (inches)</Label>
                <Input
                  value={newMapping.width_inches}
                  onChange={(e) => setNewMapping({ ...newMapping, width_inches: e.target.value })}
                  placeholder="e.g. 8"
                  className="h-9"
                  data-testid="new-mapping-width"
                />
              </div>
              <div>
                <Label className="text-xs">Design Height (inches)</Label>
                <Input
                  value={newMapping.height_inches}
                  onChange={(e) => setNewMapping({ ...newMapping, height_inches: e.target.value })}
                  placeholder="e.g. 10"
                  className="h-9"
                  data-testid="new-mapping-height"
                />
              </div>
              <div>
                <Label className="text-xs">Placement</Label>
                <select
                  value={newMapping.placement_sku}
                  onChange={(e) => setNewMapping({ ...newMapping, placement_sku: e.target.value })}
                  className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm"
                  data-testid="new-mapping-placement"
                >
                  {PLACEMENT_OPTIONS.map((o) => (
                    <option key={o.key} value={o.key}>{o.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <Button onClick={saveMapping} disabled={saving || !newMapping.size_key || !newMapping.sku} className="mt-3 bg-accent hover:bg-accent/90" data-testid="save-mapping-btn">
              {saving ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Plus size={14} className="mr-1.5" />}
              Save Mapping
            </Button>
          </div>
        </div>

        {/* How it works */}
        <div className="bg-card border border-border rounded-lg p-6" data-testid="qikink-info-section">
          <h2 className="font-sans text-sm uppercase tracking-[0.15em] font-semibold text-muted-foreground mb-3">How It Works</h2>
          <ol className="text-sm text-muted-foreground space-y-2 list-decimal list-inside">
            <li>Set up your products on <a href="https://dashboard.qikink.com" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">Qikink Dashboard</a> and note their SKUs</li>
            <li>Map each print size above to the corresponding Qikink SKU</li>
            <li>When an Indian customer places an order, it automatically submits to Qikink</li>
            <li>Qikink handles printing, packaging, and shipping with tracking</li>
            <li>Unmapped sizes fall back to manual fulfillment (admin places order manually)</li>
          </ol>
        </div>
      </div>
    </div>
  );
}
